import { AlertCircle, CheckCircle2, SearchIcon, UserCheck } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Button } from "src/components/buttons/shadcn-button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "src/components/dialogue/radix-dialog";
import { Input } from "src/components/input/input";
import { Customer } from "src/redux/types/_customer.type";
import { cn } from "src/utils";
import { useDebounce } from "use-debounce";
import { useEmployees } from "../hooks/useEmployees";
import { useAssignCustomer } from "../hooks/useAssignMutation";

const SEARCH_TYPES = [
  { value: "name", label: "Name" },
  { value: "email", label: "Email" },
  { value: "employee_code", label: "Code" },
] as const;

type SearchType = (typeof SEARCH_TYPES)[number]["value"];

const AssignCustomerModal = ({
  customer,
  isOpen,
  close,
}: {
  customer: Customer;
  isOpen: boolean;
  close: () => void;
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState<number | null>(null);
  const [searchType, setSearchType] = useState<SearchType>("name");
  const [debouncedSearch] = useDebounce(searchTerm, 500);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
      setSelectedEmployee(null);
      setSearchType("name");
    }
  }, [isOpen]);

  const buildParams = useCallback(() => {
    if (!debouncedSearch) return {};
    return { [searchType]: debouncedSearch };
  }, [debouncedSearch, searchType]);

  const {
    data: employees = [],
    isLoading,
    isError,
  } = useEmployees(buildParams());

  const selectedEmployeeData = employees.find(
    (emp) => emp.id === selectedEmployee,
  );

  const { mutateAsync: assign, isPending } = useAssignCustomer();

  const handleAssign = async () => {
    if (!selectedEmployee || !selectedEmployeeData) {
      toast.error("Please select an employee to assign");
      return;
    }

    assign(
      { employeeId: selectedEmployee, customerId: customer.id },
      {
        onSuccess: () => {
          toast.success(
            `${customer.first_name} ${customer.last_name} assigned to ${selectedEmployeeData.full_name}`,
          );
          close();
        },
        onError: () => {
          toast.error("Failed to assign customer. Please try again.");
          close();
        },
      },
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={close}>
      <DialogContent className="p-0 gap-0 max-w-md overflow-hidden rounded-xl border border-border/60 shadow-xl bg-white/70">
        {/* Header */}
        <DialogHeader className="px-5 pt-5 pb-4 border-b border-border/50 bg-white/80">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary/10 shrink-0">
                <UserCheck className="w-4.5 h-4.5 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-[12px] font-semibold leading-snug">
                  Assign Customer
                </DialogTitle>
                <p className="text-md text-primary mt-0.5 leading-snug capitalize">
                  {customer?.first_name} {customer?.last_name}
                </p>
              </div>
            </div>
          </div>
        </DialogHeader>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {/* Search type toggle */}
          <div className="space-y-2">
            <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
              Search by
            </p>
            <div className="flex gap-1.5 p-1 bg-muted/50 rounded-lg border border-border/40">
              {SEARCH_TYPES.map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => {
                    setSearchType(value);
                    setSearchTerm("");
                  }}
                  className={cn(
                    "flex-1 py-1.5 text-[12px] font-medium rounded-md transition-all duration-150",
                    searchType === value
                      ? "bg-background text-foreground shadow-sm border border-border/60"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Search input */}
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={`Search by ${searchType.replace("_", " ")}…`}
              className="pl-9 h-9 text-sm bg-muted/30 border-border/50 focus-visible:ring-1 focus-visible:ring-primary/40"
            />
            {isLoading && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="w-3.5 h-3.5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
              </div>
            )}
          </div>

          {/* Employee list */}
          <div className="min-h-[140px] max-h-[200px] overflow-y-auto rounded-lg border border-border/50 bg-muted/20">
            {isError ? (
              <div className="flex flex-col items-center justify-center h-[140px] gap-2 text-destructive">
                <AlertCircle className="w-5 h-5" />
                <p className="text-[12px] font-medium">
                  Failed to load employees
                </p>
              </div>
            ) : employees.length > 0 ? (
              <ul className="divide-y divide-border/40">
                {employees.map((employee) => {
                  const isSelected = selectedEmployee === employee.id;
                  return (
                    <li
                      key={employee.id}
                      onClick={() =>
                        setSelectedEmployee(isSelected ? null : employee.id)
                      }
                      className={cn(
                        "flex items-center justify-between px-3 py-2.5 cursor-pointer transition-colors duration-100",
                        isSelected
                          ? "bg-primary/20 hover:bg-primary/10"
                          : "hover:bg-muted/60",
                      )}
                    >
                      <div className="flex items-center gap-2.5">
                        <div
                          className={cn(
                            "w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold shrink-0",
                            isSelected
                              ? "bg-primary text-white"
                              : "bg-muted text-muted-foreground",
                          )}
                        >
                          {employee.full_name?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-[13px] font-medium text-primary leading-tight capitalize">
                            {employee.full_name}
                          </p>
                          {employee.email && (
                            <p className="text-[11px] text-muted-foreground leading-tight mt-0.5">
                              {employee.email}
                            </p>
                          )}
                        </div>
                      </div>
                      {isSelected && (
                        <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="flex flex-col items-center justify-center h-[140px] gap-1.5">
                <SearchIcon className="w-5 h-5 text-muted-foreground/50" />
                <p className="text-[12px] text-muted-foreground">
                  {debouncedSearch
                    ? "No employees found"
                    : `Search to find employees`}
                </p>
              </div>
            )}
          </div>

          {/* Selected employee pill */}
          {selectedEmployeeData && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-400">
              <CheckCircle2 className="w-3.5 h-3.5 text-primary shrink-0" />
              <p className="text-[12px] ">
                Confirm Assign{" "}
                <span className="font-semibold capitalize text-primary">
                  {customer.first_name}
                </span>{" "}
                to{" "}
                <span className="font-semibold capitalize text-primary">
                  {selectedEmployeeData.full_name}
                </span>
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-border/50 bg-muted/20">
          <DialogClose>
            <Button
              variant="outline"
              size="sm"
              className="h-8 px-4 text-[13px]"
              onClick={close}
            >
              Cancel
            </Button>
          </DialogClose>
          <Button
            size="sm"
            onClick={handleAssign}
            disabled={!selectedEmployee || isPending}
            className="h-8 px-4 text-[13px] min-w-[80px]"
          >
            {isPending ? (
              <div className="w-3.5 h-3.5 border-2 border-primary-foreground/40 border-t-primary-foreground rounded-full animate-spin" />
            ) : (
              "Confirm"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AssignCustomerModal;
